// Tab "Classificação" do EditUserModal (PO1-2023).
//
// Permite o admin definir o tipo do usuário:
//   1 — Admin (sem vínculo específico)
//   2 — Embarcador (vincula a um IdEmbarcador)
//   3 — Transportadora (vincula a um IdTransportadora)
//
// O backend valida e revoga refresh tokens ao salvar — usuário precisa
// re-logar para o JWT carregar os novos claims.

import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Button,
  FormGroup,
  Label,
} from "reactstrap";
import Select from "react-select";

import {
  listClassificacaoOptions,
  getUserClassificacao,
  setUserClassificacao,
} from "services/usersApi";
import { useToast } from "hooks/toast";

const TIPO = {
  ADMIN: 1,
  EMBARCADOR: 2,
  TRANSPORTADORA: 3,
};

const selectDarkStyles = {
  control: (base, state) => ({
    ...base,
    background: "#273046",
    borderColor: state.isFocused ? "#1d8cf8" : "#2b3553",
    boxShadow: state.isFocused ? "0 0 0 0.15rem rgba(29,140,248,0.2)" : "none",
    fontSize: 13,
    minHeight: 32,
  }),
  menu: (base) => ({ ...base, background: "#273046", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    background: isSelected ? "#1d8cf8" : isFocused ? "#2b3553" : "#273046",
    color: "#f5f7fb",
    fontSize: 13,
  }),
  singleValue: (base) => ({ ...base, color: "#f5f7fb" }),
  input: (base) => ({ ...base, color: "#f5f7fb" }),
  placeholder: (base) => ({ ...base, color: "#8fa3c8" }),
};

function buildLabel(item) {
  const fantasia = item.Fantasia || item.RazaoSocial || `(${item.Id})`;
  const cnpj = item.CNPJ_CPF || "";
  return cnpj ? `${fantasia} — ${cnpj}` : fantasia;
}

export default function UserClassificacaoTab({ user, onSaved }) {
  const [tipo, setTipo] = useState(TIPO.ADMIN);
  const [embarcadorId, setEmbarcadorId] = useState(null);
  const [transportadoraId, setTransportadoraId] = useState(null);
  const [embarcadores, setEmbarcadores] = useState([]);
  const [transportadoras, setTransportadoras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [original, setOriginal] = useState({
    tipo: TIPO.ADMIN,
    embarcadorId: null,
    transportadoraId: null,
  });
  const { addToast } = useToast();

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([listClassificacaoOptions(), getUserClassificacao(user.id)])
      .then(([options, current]) => {
        if (cancelled) return;
        setEmbarcadores(options.embarcadores || []);
        setTransportadoras(options.transportadoras || []);
        const t = current.IdClassificacaoPessoa || TIPO.ADMIN;
        const eId = current.IdEmbarcador || null;
        const tId = current.IdTransportadora || null;
        setTipo(t);
        setEmbarcadorId(eId);
        setTransportadoraId(tId);
        setOriginal({ tipo: t, embarcadorId: eId, transportadoraId: tId });
      })
      .catch((err) => {
        console.error("[UserClassificacaoTab] load:", err);
        addToast({
          place: "tr",
          type: "danger",
          title: "Erro",
          description: "Não foi possível carregar classificação",
          icon: "tim-icons icon-alert-circle-exc",
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, addToast]);

  const embarcadorOptions = useMemo(
    () => embarcadores.map((e) => ({ value: e.Id, label: buildLabel(e) })),
    [embarcadores]
  );
  const transportadoraOptions = useMemo(
    () => transportadoras.map((t) => ({ value: t.Id, label: buildLabel(t) })),
    [transportadoras]
  );

  const isDirty =
    tipo !== original.tipo ||
    embarcadorId !== original.embarcadorId ||
    transportadoraId !== original.transportadoraId;

  const canSave =
    !saving &&
    isDirty &&
    (tipo === TIPO.ADMIN ||
      (tipo === TIPO.EMBARCADOR && !!embarcadorId) ||
      (tipo === TIPO.TRANSPORTADORA && !!transportadoraId));

  const handleTipo = useCallback((t) => {
    setTipo(t);
    // Limpa selects que não pertencem ao tipo escolhido
    if (t === TIPO.ADMIN) {
      setEmbarcadorId(null);
      setTransportadoraId(null);
    } else if (t === TIPO.EMBARCADOR) {
      setTransportadoraId(null);
    } else if (t === TIPO.TRANSPORTADORA) {
      setEmbarcadorId(null);
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!user) return;
    setSaving(true);
    try {
      const payload = {
        idClassificacaoPessoa: tipo,
        idEmbarcador: tipo === TIPO.EMBARCADOR ? embarcadorId : null,
        idTransportadora: tipo === TIPO.TRANSPORTADORA ? transportadoraId : null,
      };
      await setUserClassificacao(user.id, payload);
      addToast({
        place: "tr",
        type: "success",
        title: "Salvo",
        description:
          "Classificação atualizada. O usuário precisará re-logar para os novos claims surtirem efeito.",
        icon: "tim-icons icon-check-2",
      });
      setOriginal({ tipo, embarcadorId, transportadoraId });
      if (onSaved) onSaved(payload);
    } catch (err) {
      console.error("[UserClassificacaoTab] save:", err);
      const message =
        (err.response && err.response.data && err.response.data.error) ||
        "Erro ao salvar classificação";
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro",
        description: message,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setSaving(false);
    }
  }, [user, tipo, embarcadorId, transportadoraId, addToast, onSaved]);

  if (loading) {
    return (
      <div style={{ color: "#8fa3c8", textAlign: "center", padding: 20 }}>
        Carregando classificação…
      </div>
    );
  }

  return (
    <div>
      <FormGroup>
        <Label style={{ fontWeight: 600 }}>Tipo</Label>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 24,
            marginTop: 8,
          }}
        >
          {[
            { value: TIPO.ADMIN, label: "Admin" },
            { value: TIPO.EMBARCADOR, label: "Embarcador" },
            { value: TIPO.TRANSPORTADORA, label: "Transportadora" },
          ].map((opt) => (
            <label
              key={opt.value}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                color: "#f5f7fb",
                cursor: "pointer",
                marginBottom: 0,
              }}
            >
              <input
                type="radio"
                name="user-classificacao-tipo"
                checked={tipo === opt.value}
                onChange={() => handleTipo(opt.value)}
                style={{ margin: 0, position: "static" }}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </FormGroup>

      {tipo === TIPO.EMBARCADOR && (
        <FormGroup style={{ marginTop: 16 }}>
          <Label>Embarcador</Label>
          <Select
            styles={selectDarkStyles}
            options={embarcadorOptions}
            value={
              embarcadorOptions.find((o) => o.value === embarcadorId) || null
            }
            onChange={(opt) => setEmbarcadorId(opt ? opt.value : null)}
            placeholder="Selecione um embarcador..."
            isClearable
            menuPortalTarget={document.body}
          />
        </FormGroup>
      )}

      {tipo === TIPO.TRANSPORTADORA && (
        <FormGroup style={{ marginTop: 16 }}>
          <Label>Transportadora</Label>
          <Select
            styles={selectDarkStyles}
            options={transportadoraOptions}
            value={
              transportadoraOptions.find((o) => o.value === transportadoraId) ||
              null
            }
            onChange={(opt) => setTransportadoraId(opt ? opt.value : null)}
            placeholder="Selecione uma transportadora..."
            isClearable
            menuPortalTarget={document.body}
          />
        </FormGroup>
      )}

      <div
        style={{
          color: "#8fa3c8",
          fontSize: 11,
          marginTop: 16,
          lineHeight: 1.5,
        }}
      >
        Após salvar, as sessões existentes do usuário serão revogadas — ele
        precisará fazer login novamente para os novos vínculos refletirem nos
        claims do JWT.
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 16,
        }}
      >
        <Button color="success" onClick={handleSave} disabled={!canSave}>
          {saving ? "Salvando…" : "Salvar classificação"}
        </Button>
      </div>
    </div>
  );
}
